"use client";

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Sparkles,
    ArrowLeft,
    Gift,
    Send,
    ShieldCheck,
    MessageCircleWarning,
    Check,
    Copy,
    CheckCircle2,
    Smartphone,
    Instagram
} from "lucide-react";

const REASONS = [
    { value: "premium_not_enough", title: "Premium yeterli gelmedi", desc: "Beklediğim özellikler yoktu" },
    { value: "chat_problem", title: "Chat'te sorun yaşadım", desc: "Mesajlar gecikti veya hata aldım" },
    { value: "too_expensive", title: "Fiyat yüksek geldi", desc: "Şu an bütçeme uymadı" },
    { value: "not_using", title: "Yeterince kullanmıyorum", desc: "Şu ara ihtiyacım kalmadı" },
    { value: "technical_issue", title: "Teknik sorun / bug", desc: "Performans veya premium özellikler çalışmadı" },
    { value: "found_alternative", title: "Alternatif buldum", desc: "Başka bir uygulamaya geçtim" },
    { value: "other", title: "Diğer", desc: "Farklı bir sebep" },
];

const BRAND = "#ff9f43";

export default function TrialExitSurveyPage() {
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get("t") || "", [searchParams]);

    const [selectedReasons, setSelectedReasons] = useState([]);
    const [details, setDetails] = useState("");
    const [feedback, setFeedback] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [premiumCode, setPremiumCode] = useState("");
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");

    const canSubmit = !!token && selectedReasons.length > 0 && !loading && !submitted;

    function copyCode() {
        if (!premiumCode) return;
        navigator.clipboard.writeText(premiumCode).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        });
    }

    function toggleReason(value) {
        setSelectedReasons((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    }

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        if (!token) {
            setError("Bu sayfa sadece e-postandaki özel link ile açılabilir.");
            return;
        }
        if (selectedReasons.length === 0) {
            setError("Lütfen en az bir seçenek seç.");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch("https://api.whatsin-app.com/trial-exit-survey", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    token,
                    reason: selectedReasons.join(","),
                    details: details || null,
                    feedback: feedback || null,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data?.message || "Bir hata oluştu. Lütfen tekrar dene.");
                return;
            }
            if (data.premium_code) {
                setPremiumCode(data.premium_code);
            }
            setSubmitted(true);
        } catch {
            setError("Bağlantı hatası. Lütfen tekrar dene.");
        } finally {
            setLoading(false);
        }
    }

    /* ── Inline Styles ── */

    const pageStyle = {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#FDFBF8",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: "#1a1a1a",
        WebkitFontSmoothing: "antialiased",
    };

    const cardStyle = {
        backgroundColor: "#fff",
        borderRadius: 20,
        border: "1px solid #f0ede8",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(255,159,67,0.06)",
        padding: "clamp(24px, 5vw, 40px) clamp(20px, 5vw, 36px)",
    };

    const textareaBase = {
        width: "100%",
        boxSizing: "border-box",
        padding: "14px 16px",
        borderRadius: 12,
        border: "1.5px solid #ebe8e3",
        backgroundColor: "#fafaf8",
        fontSize: 15,
        lineHeight: 1.6,
        color: "#1a1a1a",
        outline: "none",
        transition: "border-color 0.2s, box-shadow 0.2s, background-color 0.2s",
        resize: "vertical",
        fontFamily: "inherit",
    };

    function reasonStyle(active) {
        return {
            display: "flex",
            alignItems: "center",
            gap: "clamp(10px, 3vw, 14px)",
            padding: "clamp(12px, 3vw, 14px) clamp(14px, 3vw, 18px)",
            borderRadius: 14,
            border: active ? "2px solid " + BRAND : "1.5px solid #ebe8e3",
            backgroundColor: active ? "#FFF8F0" : "#fff",
            boxShadow: active ? "0 0 0 3px rgba(255,159,67,0.08)" : "none",
            cursor: "pointer",
            transition: "all 0.2s ease",
            width: "100%",
            textAlign: "left",
            fontFamily: "inherit",
        };
    }

    function checkboxStyle(active) {
        return {
            width: 20,
            height: 20,
            borderRadius: 6,
            border: active ? "none" : "2px solid #ccc8c2",
            backgroundColor: active ? BRAND : "transparent",
            transition: "all 0.2s ease",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        };
    }

    const focusTextarea = (e) => {
        e.currentTarget.style.borderColor = BRAND;
        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(255,159,67,0.08)";
        e.currentTarget.style.backgroundColor = "#fff";
    };

    const blurTextarea = (e) => {
        e.currentTarget.style.borderColor = "#ebe8e3";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.backgroundColor = "#fafaf8";
    };

    const responsiveCSS = `
    .survey-container {
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(16px, 4vw, 24px);
    }
    .survey-inner {
      max-width: 620px;
      margin: 0 auto;
      padding-top: clamp(20px, 4vw, 36px);
      padding-bottom: clamp(36px, 6vw, 60px);
      position: relative;
    }
    .survey-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: clamp(14px, 3vw, 24px) clamp(16px, 4vw, 24px);
      border-bottom: 1px solid rgba(0,0,0,0.05);
    }
    .survey-logo-text {
      font-size: clamp(20px, 4vw, 24px);
      font-weight: 900;
      letter-spacing: -0.02em;
    }
    .survey-back-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #fff;
      padding: clamp(10px, 2vw, 12px) clamp(16px, 3vw, 24px);
      border-radius: 14px;
      font-weight: 600;
      font-size: clamp(13px, 2vw, 15px);
      text-decoration: none;
      transition: all 0.2s;
      box-shadow: 0 2px 12px rgba(255,159,67,0.25);
    }
    .survey-back-btn:hover {
      box-shadow: 0 4px 20px rgba(255,159,67,0.35);
      transform: translateY(-1px);
    }
    .survey-back-btn:active {
      transform: scale(0.97);
    }
    .survey-back-label-full { display: inline; }
    .survey-back-label-short { display: none; }
    @media (max-width: 480px) {
      .survey-back-label-full { display: none; }
      .survey-back-label-short { display: inline; }
    }
    .survey-hero {
      text-align: center;
      margin-bottom: clamp(20px, 4vw, 32px);
    }
    .survey-title {
      font-size: clamp(24px, 5vw, 40px);
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -0.025em;
      margin: 12px 0 0;
      color: #1a1a1a;
    }
    .survey-subtitle {
      margin-top: 10px;
      font-size: clamp(14px, 2.5vw, 17px);
      line-height: 1.65;
      color: #7a7672;
      max-width: 480px;
      margin-left: auto;
      margin-right: auto;
    }
    .survey-reasons-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .survey-reason-title {
      display: block;
      font-size: clamp(13px, 2.5vw, 15px);
      line-height: 1.35;
    }
    .survey-reason-desc {
      display: block;
      font-size: clamp(11px, 2vw, 13px);
      color: #9a9691;
      margin-top: 2px;
    }
    .survey-textareas {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin-bottom: 32px;
    }
    .survey-label-row {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .survey-submit-btn {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: clamp(14px, 3vw, 16px) 24px;
      border-radius: 14px;
      border: none;
      font-size: clamp(14px, 2.5vw, 16px);
      font-weight: 600;
      font-family: inherit;
      transition: all 0.2s ease;
    }
    .survey-submit-btn:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 24px rgba(255,159,67,0.35);
    }
    .survey-submit-btn:active:not(:disabled) {
      transform: scale(0.98);
    }
    .survey-success-actions {
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: center;
    }
    @media (min-width: 480px) {
      .survey-success-actions {
        flex-direction: row;
        justify-content: center;
      }
    }
    .survey-success-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 14px 32px;
      border-radius: 14px;
      font-weight: 600;
      font-size: 15px;
      text-decoration: none;
      transition: all 0.2s;
      width: 100%;
    }
    @media (min-width: 480px) {
      .survey-success-btn {
        width: auto;
      }
    }
    .survey-success-btn:hover {
      transform: translateY(-1px);
    }
    .survey-footer-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: clamp(24px, 4vw, 48px);
      align-items: center;
    }
    @media (min-width: 640px) {
      .survey-footer-grid {
        grid-template-columns: 1fr 1fr;
      }
    }
    .survey-footer-left {
      text-align: center;
    }
    @media (min-width: 640px) {
      .survey-footer-left {
        text-align: left;
      }
    }
    .survey-footer-logo-row {
      display: flex;
      align-items: center;
      gap: 12px;
      justify-content: center;
    }
    @media (min-width: 640px) {
      .survey-footer-logo-row {
        justify-content: flex-start;
      }
    }
    .survey-footer-links {
      display: flex;
      flex-wrap: wrap;
      gap: clamp(16px, 3vw, 32px);
      justify-content: center;
    }
    @media (min-width: 640px) {
      .survey-footer-links {
        justify-content: flex-end;
      }
    }
    .survey-footer-link {
      color: #9CA3AF;
      text-decoration: none;
      font-weight: 800;
      font-size: clamp(14px, 2.5vw, 18px);
      transition: all 0.2s;
    }
    .survey-footer-link:hover {
      color: #fff;
      transform: translateY(-2px);
    }
    .survey-glow {
      position: absolute;
      top: -60px;
      left: 50%;
      transform: translateX(-50%);
      width: min(700px, 100vw);
      height: 500px;
      border-radius: 50%;
      background: radial-gradient(ellipse at center, ${BRAND} 0%, transparent 70%);
      opacity: 0.04;
      pointer-events: none;
    }
  `;

    return (
        <div style={pageStyle}>
            <style>{responsiveCSS}</style>

            {/* ═══ HEADER (UNTOUCHED) ═══ */}
            <header className="survey-header">
                <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
                    <img
                        src="/assets/icon.png"
                        alt="logo"
                        style={{ borderRadius: 14, width: 40, height: 40, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}
                    />
                    <span className="survey-logo-text">
                        <span style={{ color: BRAND }}>whatsin</span>
                        <span style={{ color: BRAND }}>?</span>
                    </span>
                </Link>
                <Link
                    to="/"
                    className="survey-back-btn"
                    style={{ backgroundColor: BRAND }}
                >
                    <ArrowLeft style={{ width: 18, height: 18 }} />
                    <span className="survey-back-label-full">{"Geri Dön"}</span>
                    <span className="survey-back-label-short">{"Geri"}</span>
                </Link>
            </header>

            {/* ═══ MAIN ═══ */}
            <main style={{ flex: 1, position: "relative" }}>
                <div className="survey-glow" aria-hidden="true" />

                <div className="survey-container">
                    <div className="survey-inner">

                        {/* Hero */}
                        <div className="survey-hero">
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "5px 14px",
                                    borderRadius: 100,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    letterSpacing: "0.02em",
                                    backgroundColor: "#FFF3E0",
                                    color: BRAND,
                                }}
                            >
                                <Sparkles style={{ width: 14, height: 14 }} />
                                {"Geri Bildirim"}
                            </span>

                            <h1 className="survey-title">
                                {"Fikrin Bizim İçin "}
                                <span style={{ color: BRAND }}>{"Çok Kıymetli"}</span>
                            </h1>

                            <p className="survey-subtitle">
                                {"Deneyiminizi iyileştirmemize yardımcı olun, biz de size teşekkür olarak "}
                                <strong style={{ color: BRAND, fontWeight: 700 }}>{"1 ay Premium"}</strong>
                                {" üyelik kodu hediye edelim."}
                            </p>
                        </div>

                        {/* Card */}
                        <div style={cardStyle}>
                            {submitted ? (
                                <div style={{ padding: "clamp(8px, 3vw, 20px) 0" }}>
                                    {/* Thank you header */}
                                    <div style={{ textAlign: "center", marginBottom: 28 }}>
                                        <div
                                            style={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 16,
                                                backgroundColor: "#FFF3E0",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                margin: "0 auto 16px",
                                            }}
                                        >
                                            <Gift style={{ width: 32, height: 32, color: BRAND }} />
                                        </div>
                                        <h2 style={{ fontSize: "clamp(20px, 4vw, 24px)", fontWeight: 700, margin: "0 0 6px", color: "#1a1a1a" }}>
                                            {"Teşekkürler!"}
                                        </h2>
                                        <p style={{ fontSize: "clamp(14px, 2.5vw, 16px)", color: "#7a7672", lineHeight: 1.6, margin: 0 }}>
                                            {"Geri bildirimin için teşekkür ederiz. İşte sana hediye Premium kodun:"}
                                        </p>
                                    </div>

                                    {/* Coupon Code */}
                                    {premiumCode && (
                                        <div style={{ marginBottom: 28 }}>
                                            <button
                                                type="button"
                                                onClick={copyCode}
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 12,
                                                    padding: "clamp(14px, 3vw, 18px) 20px",
                                                    borderRadius: 14,
                                                    border: "2px dashed " + BRAND,
                                                    backgroundColor: "#FFFAF4",
                                                    cursor: "pointer",
                                                    fontFamily: "inherit",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontFamily: "'SF Mono', 'Fira Code', 'Courier New', monospace",
                                                        fontSize: "clamp(14px, 3vw, 18px)",
                                                        fontWeight: 700,
                                                        letterSpacing: "0.08em",
                                                        color: "#1a1a1a",
                                                        wordBreak: "break-all",
                                                    }}
                                                >
                                                    {premiumCode}
                                                </span>
                                                {copied ? (
                                                    <CheckCircle2 style={{ width: 20, height: 20, color: "#22c55e", flexShrink: 0 }} />
                                                ) : (
                                                    <Copy style={{ width: 20, height: 20, color: BRAND, flexShrink: 0 }} />
                                                )}
                                            </button>
                                            <p
                                                style={{
                                                    textAlign: "center",
                                                    marginTop: 8,
                                                    fontSize: 12,
                                                    color: copied ? "#22c55e" : "#b0ada8",
                                                    fontWeight: 500,
                                                    transition: "color 0.2s",
                                                }}
                                            >
                                                {copied ? "Kopyalandı!" : "Kopyalamak için koda tıkla"}
                                            </p>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    <div style={{ height: 1, backgroundColor: "#f0ede8", marginBottom: 24 }} />

                                    {/* Redemption Steps */}
                                    <div style={{ marginBottom: 28 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                                            <Smartphone style={{ width: 18, height: 18, color: BRAND }} />
                                            <span style={{ fontSize: "clamp(14px, 2.5vw, 16px)", fontWeight: 700, color: "#1a1a1a" }}>
                                                {"Kodu Nasıl Kullanırım?"}
                                            </span>
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                            {[
                                                "Google Play Store'u aç",
                                                "Profil ikonuna dokun \u2192 \u00D6demeler ve abonelikler",
                                                "\u201CKod kullan\u201D se\u00E7ene\u011Fine gir",
                                                "Yukar\u0131daki kodu yap\u0131\u015Ft\u0131r \u2192 Onayla",
                                            ].map((step, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 12,
                                                        padding: "12px 0",
                                                        borderBottom: i < 3 ? "1px solid #f5f3ef" : "none",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            borderRadius: 8,
                                                            backgroundColor: i === 3 ? BRAND : "#FFF3E0",
                                                            color: i === 3 ? "#fff" : BRAND,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            flexShrink: 0,
                                                            marginTop: 1,
                                                        }}
                                                    >
                                                        {i + 1}
                                                    </span>
                                                    <span style={{ fontSize: "clamp(13px, 2.5vw, 15px)", color: "#444", lineHeight: 1.5, fontWeight: 500 }}>
                                                        {step}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="survey-success-actions">
                                        <Link
                                            to="/"
                                            className="survey-success-btn"
                                            style={{ backgroundColor: "#f5f3ef", color: "#3d3d3d" }}
                                        >
                                            {"Ana Sayfaya Dön"}
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit}>
                                    {!token && (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 12,
                                                padding: "14px 18px",
                                                borderRadius: 14,
                                                backgroundColor: "#FEF2F2",
                                                border: "1px solid #FECACA",
                                                color: "#B91C1C",
                                                fontSize: 14,
                                                marginBottom: 28,
                                            }}
                                        >
                                            <MessageCircleWarning style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0 }} />
                                            <div style={{ lineHeight: 1.55 }}>
                                                <p style={{ margin: 0, fontWeight: 600 }}>{"Erişim geçersiz."}</p>
                                                <p style={{ margin: "2px 0 0", opacity: 0.8 }}>
                                                    {"Lütfen e-postandaki linke tıkladığından emin ol."}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Reason Selection - Multi Select */}
                                    <fieldset style={{ border: "none", padding: 0, margin: 0, marginBottom: 32 }}>
                                        <legend
                                            style={{
                                                display: "flex",
                                                alignItems: "baseline",
                                                justifyContent: "space-between",
                                                width: "100%",
                                                marginBottom: 16,
                                                padding: 0,
                                            }}
                                        >
                                            <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
                                                {"Neden iptal ettiniz?"}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 500,
                                                    letterSpacing: "0.1em",
                                                    color: "#b0ada8",
                                                }}
                                            >
                                                {"BİRDEN FAZLA SEÇEBİLİRSİNİZ"}
                                            </span>
                                        </legend>

                                        <div className="survey-reasons-list">
                                            {REASONS.map((r) => {
                                                const active = selectedReasons.includes(r.value);
                                                return (
                                                    <button
                                                        key={r.value}
                                                        type="button"
                                                        onClick={() => toggleReason(r.value)}
                                                        style={reasonStyle(active)}
                                                    >
                                                        <span style={checkboxStyle(active)}>
                                                            {active && <Check style={{ width: 14, height: 14, color: "#fff", strokeWidth: 3 }} />}
                                                        </span>
                                                        <span style={{ flex: 1, minWidth: 0 }}>
                                                            <span
                                                                className="survey-reason-title"
                                                                style={{
                                                                    fontWeight: active ? 600 : 500,
                                                                    color: active ? "#1a1a1a" : "#444",
                                                                }}
                                                            >
                                                                {r.title}
                                                            </span>
                                                            <span className="survey-reason-desc">
                                                                {r.desc}
                                                            </span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </fieldset>

                                    {/* Divider */}
                                    <div style={{ height: 1, backgroundColor: "#f0ede8", marginBottom: 32 }} />

                                    {/* Text Inputs */}
                                    <div className="survey-textareas">
                                        <div>
                                            <div className="survey-label-row">
                                                <label style={{ fontSize: 14, fontWeight: 600, color: "#2a2a2a" }}>
                                                    {"Biraz daha detay"}
                                                </label>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        letterSpacing: "0.1em",
                                                        color: "#b0ada8",
                                                    }}
                                                >
                                                    {"OPSİYONEL"}
                                                </span>
                                            </div>
                                            <textarea
                                                value={details}
                                                onChange={(e) => setDetails(e.target.value)}
                                                rows={3}
                                                placeholder="Premium'da ne eksikti? Sizi ne memnun etmedi?"
                                                style={textareaBase}
                                                onFocus={focusTextarea}
                                                onBlur={blurTextarea}
                                            />
                                        </div>

                                        <div>
                                            <div className="survey-label-row">
                                                <label style={{ fontSize: 14, fontWeight: 600, color: "#2a2a2a" }}>
                                                    {"Neyi iyileştirelim?"}
                                                </label>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        letterSpacing: "0.1em",
                                                        color: "#b0ada8",
                                                    }}
                                                >
                                                    {"OPSİYONEL"}
                                                </span>
                                            </div>
                                            <textarea
                                                value={feedback}
                                                onChange={(e) => setFeedback(e.target.value)}
                                                rows={3}
                                                placeholder="Şu özellik olsaydı tekrar denerdim dediğiniz bir şey var mı?"
                                                style={textareaBase}
                                                onFocus={focusTextarea}
                                                onBlur={blurTextarea}
                                            />
                                        </div>
                                    </div>

                                    {/* Error */}
                                    {error && (
                                        <div
                                            style={{
                                                textAlign: "center",
                                                fontSize: 14,
                                                fontWeight: 500,
                                                marginBottom: 16,
                                                padding: "12px 18px",
                                                borderRadius: 12,
                                                backgroundColor: "#FEF2F2",
                                                color: "#DC2626",
                                                border: "1px solid #FECACA",
                                            }}
                                        >
                                            {error}
                                        </div>
                                    )}

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        className="survey-submit-btn"
                                        style={{
                                            cursor: canSubmit ? "pointer" : "not-allowed",
                                            backgroundColor: canSubmit ? BRAND : "#e5e2dd",
                                            color: canSubmit ? "#fff" : "#a8a5a0",
                                            boxShadow: canSubmit ? "0 4px 20px rgba(255,159,67,0.25)" : "none",
                                        }}
                                    >
                                        <Send style={{ width: 18, height: 18 }} />
                                        {loading ? "Gönderiliyor..." : "Gönder ve Ödülünü Al"}
                                    </button>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 6,
                                            marginTop: 18,
                                        }}
                                    >
                                        <ShieldCheck style={{ width: 14, height: 14, color: "#6BBF7B" }} />
                                        <span style={{ fontSize: 12, fontWeight: 500, color: "#b0ada8", letterSpacing: "0.01em" }}>
                                            {"Verileriniz güvende ve gizli kalır"}
                                        </span>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <footer className="bg-gray-900 text-white py-12">
                <div className="container mx-auto px-4">
                    <div className="grid sm:grid-cols-2 gap-8 items-center">
                        <div className="space-y-4 text-center sm:text-left">
                            <div className="flex items-center gap-3 justify-center sm:justify-start">
                                <img src="/assets/icon.png" alt="whatsin? logo" className="rounded-lg w-10 h-10" />
                                <span className="text-2xl font-bold text-white">
                                    whatsin?
                                </span>
                            </div>
                            <p className="text-gray-400">Güzellik dünyasında akıllı rehberin! 💄✨</p>
                        </div>
                        <div className="flex flex-wrap gap-4 justify-center sm:justify-end items-center">
                            <a
                                href="https://www.instagram.com/whatsin_app/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors no-underline flex items-center gap-2"
                            >
                                <Instagram className="w-5 h-5" />
                                <span className="hidden sm:inline">Instagram</span>
                            </a>
                            <Link to="/support" className="text-gray-400 hover:text-white transition-colors no-underline">
                                İletişim
                            </Link>
                            <Link to="/legal" className="text-gray-400 hover:text-white transition-colors no-underline">
                                Kullanım Koşulları
                            </Link>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
                        <p>&copy; 2026 whatsin Tüm hakları saklıdır. Made with 💖 for healthy living.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
